// EditDetailsDialog (job page): the job's own details, on a FormDialog: its name, type, department, priority,
// the customer's PO and its tags. The name is required; a PO is letters, numbers and dashes. Saving,
// a save that fails and closing with changes are FormDialog's.
import { useState } from 'react';
import { FormDialog } from '../components/Dialog/Dialog.tsx';
import { SelectField, TextField } from '../components/Field/Field.tsx';

export interface JobDetails {
  name: string;
  type: string;
  team: string;
  priority: string;
  po: string;
  tags: string;
}
export type Options = { value: string; label: string }[];

export interface EditDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** The job number, for its name to screen readers. */
  job: string;
  details: JobDetails;
  types: Options;
  teams: Options;
  /** Saves; a rejection says it didn't save and keeps the changes. */
  onSave: (d: JobDetails) => Promise<void> | void;
  /** Storybook only. */
  previewState?: 'failed' | 'discard';
}

const PRIORITIES: Options = [
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent: today' },
];

/** What's wrong with each field, or nothing. */
export function detailsErrors(d: JobDetails): Partial<Record<keyof JobDetails, string>> {
  const e: Partial<Record<keyof JobDetails, string>> = {};
  if (!d.name.trim()) e.name = 'Give the job a name, so the techs know what it is.';
  if (d.po && !/^[A-Za-z0-9-]+$/.test(d.po.trim()))
    e.po = 'Letters, numbers and dashes only, as on their paperwork.';
  return e;
}

export function EditDetailsDialog({
  isOpen,
  onClose,
  job,
  details,
  types,
  teams,
  onSave,
  previewState,
}: EditDetailsDialogProps) {
  const [v, setV] = useState(details);
  const [tried, setTried] = useState(false);
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setV(details);
      setTried(false);
    }
  }
  const err = detailsErrors(v);
  const show = (k: keyof JobDetails) => (tried ? (err[k] ?? null) : null);
  const dirty = (Object.keys(details) as (keyof JobDetails)[]).some((k) => v[k] !== details[k]);
  const set = (k: keyof JobDetails) => (value: string) => setV((d) => ({ ...d, [k]: value }));
  return (
    <FormDialog
      title="Edit details"
      label={`Edit job ${job}’s details`}
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      isDirty={dirty}
      previewState={previewState}
      validate={() => {
        setTried(true);
        return !Object.keys(err).length;
      }}
      onSubmit={async () => {
        await onSave({ ...v, name: v.name.trim(), po: v.po.trim() });
      }}
    >
      <div data-span="all">
        <TextField label="Job name" isRequired value={v.name} onChange={set('name')} error={show('name')} />
      </div>
      <SelectField
        label="Job type"
        value={v.type}
        onChange={set('type')}
        options={types}
        description="Sets the price book and the forms the tech fills in."
      />
      <SelectField label="Department" value={v.team} onChange={set('team')} options={teams} />
      <SelectField label="Priority" value={v.priority} onChange={set('priority')} options={PRIORITIES} />
      <TextField
        label="Customer’s PO"
        placeholder="e.g. 4471-B"
        value={v.po}
        onChange={set('po')}
        error={show('po')}
        description="Printed on the invoice."
      />
      <div data-span="all">
        <TextField
          label="Tags"
          placeholder="e.g. warranty, gas permit"
          value={v.tags}
          onChange={set('tags')}
          description="Separate them with commas. The board and the job list filter by them."
        />
      </div>
    </FormDialog>
  );
}
